import React, { useCallback, useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import {
  TmoButtons as TmoButton,
  withModal,
  TmoH3,
  RawData,
  ConfirmationModalV2,
  ExpandableText
} from '@tmobile/tmo-bps-syncup-web-component-lib';
import { ReactSVG } from 'react-svg';

import Fetcher from '../Fetcher';
import deviceService from '../../services/deviceService';
import userService from '../../services/userService';
import DeviceDetailsSection from './DeviceDetailsSection';
import HardwareDetailsGrid from './hardware/HardwareDetailsGrid';
import SensorDetails from '../sensors/SensorDetails';
import {
  IOT_PRODUCTS,
  APP_MOBILE,
  THING_TYPES
} from '../../utils/app_constants';
import Feature from '../featureWrappers/Feature';
import { DEVICE_MODELS } from '../../utils/deviceConsts';
import VehicleDetails from './thingDetails/VehicleDetails';
import ActiveUserTabContext from '../../context/ActiveUserTabContext';
import { getRandomString } from '../../utils/stringUtils';
import DeviceTwin from './device/DeviceTwin';
import ThingDetails from './thingDetails/ThingDetails';
import TaskDetails from 'components/tasks/TaskDetails';
import PetDetails from './thingDetails/PetDetails.jsx';
import { trackCustomEvents } from '../../utils/appInsight_analytics';
import StatusMark from '../textHelpers/StatusMark';
import HardwareDetailsHeader from './hardware/HardwareDetailsHeader';
import AddDeviceRoadsideServices from 'components/drive/roadsideServices/AddDeviceRoadsideServices';

import style from './Thing.module.css';
import events from '../../static/assets/icons/events_icon.svg';
import trips from '../../static/assets/icons/trips_icon.svg';
import delete_icon from '../../static/assets/icons/action/delete.svg';
import roadSide from '../../static/assets/icons/roadside_icon.svg';
import logs from '../../static/assets/icons/logs_icon.svg';
import { FaCaretDown } from 'react-icons/all';

/**
 * Thing Component - Displays detailed information about a single thing and its associated device
 *
 * This component serves as the main detail view for any "thing" in the SyncUp ecosystem:
 * - Vehicles (DRIVE product)
 * - Pets (PETS product)
 * - Trackers (TRACKER product)
 * - Watches (WATCH product)
 *
 * Features:
 * - Shows thing information with avatar/image display
 * - Displays associated device details and management
 * - Provides product-specific action buttons (Trips, Events, Logs, etc.)
 * - Handles device linking/unlinking operations
 * - Shows IoT device twin data for supported products
 * - Displays hardware details for specific device models
 * - Shows sensor information and controls
 * - Manages user tasks (for WATCH products)
 *
 * @param {Object} thing - Thing object containing all thing details and status
 * @param {Function} onAddDevice - Callback to handle device addition/linking
 * @param {Function} onRemoveDevice - Callback to handle device removal/unlinking
 * @param {Function} onRemoveThing - Callback to handle thing deletion
 * @param {Function} openModal - Modal handler from withModal HOC
 * @param {string} productType - Product type constant (PETS, TRACKER, WATCH, etc.)
 */
function Thing({
  thing,
  onAddDevice,
  onRemoveDevice,
  onRemoveThing,
  openModal,
  productType
}) {
  // ==========================================
  // CONTEXT & STATE MANAGEMENT
  // ==========================================

  /**
   * Get current user and product context for API calls
   * userId: Current logged-in user identifier
   * product: Current product being viewed (PETS, TRACKER, etc.)
   */
  const {
    selectedTab: { userId, product }
  } = useContext(ActiveUserTabContext);

  /**
   * Device twin data from IoT platform
   * Contains real-time device telemetry and configuration
   * Only loaded for IoT products (PETS, TRACKER, WATCH)
   */
  const [deviceTwin, setDeviceTwin] = useState();

  /**
   * User tasks data (for WATCH products only)
   * Contains tasks assigned to/by the watch user
   */
  const [userTasks, setUserTasks] = useState();

  /**
   * Refresh trigger key for forcing data refresh
   * Used to refresh tasks when they are updated
   */
  const [refreshKey, setRefreshKey] = useState(getRandomString());

  /**
   * Function to force refresh of user tasks
   * Updates refreshKey which triggers Fetcher to reload tasks
   */
  const forceRefresh = () => setRefreshKey(getRandomString());

  // Get router history for navigation actions
  let history = useHistory();

  // ==========================================
  // DATA FETCHING CALLBACKS
  // ==========================================

  /**
   * Callback for fetching device twin data from IoT platform
   * Only executes if thing has a currentDevice with hardwareId
   * Memoized to prevent unnecessary re-renders
   *
   * Dependencies: thing.currentDevice, product
   */
  const actionCallback = useCallback(
    () =>
      thing?.currentDevice &&
      deviceService.getDeviceTwin({
        deviceId: thing?.currentDevice?.hardwareId,
        syncUpAppName: product
      }),
    [thing.currentDevice, product]
  );

  /**
   * Callback for fetching user tasks (WATCH products only)
   * Gets tasks associated with the current thing/user
   * Memoized to prevent unnecessary re-renders
   *
   * Dependencies: userId, thing.id, product
   */
  const actionUserTaskCallback = useCallback(
    () =>
      userService.getUserTasksDetailsByThing({
        userId,
        thingId: thing.id,
        syncUpAppName: product
      }),
    [userId, thing.id, product]
  );

  // ==========================================
  // EVENT HANDLERS
  // ==========================================

  /**
   * Handles thing deletion with confirmation
   * Process:
   * 1. Show confirmation modal
   * 2. If confirmed, track analytics event
   * 3. Call parent callback to delete thing
   *
   * Analytics tracking includes thingId and name for business intelligence
   */
  const handleRemoveThingClicked = async () => {
    const { isConfirmed } = await openModal();
    if (isConfirmed) {
      // Track deletion event for analytics
      trackCustomEvents(`Remove Thing - ${thing.thingType}`, {
        thingId: thing.id,
        name: thing.name
      });

      // Call parent callback to handle actual deletion
      await onRemoveThing({ thingId: thing.id, name: thing.name });
    }
  };

  /**
   * Handles device deletion with confirmation
   * Process:
   * 1. Show confirmation modal
   * 2. If confirmed, track analytics event
   * 3. Call parent callback to delete device
   *
   * Device ID resolution varies by product:
   * - DRIVE: uses thing.device.id
   * - Other products: uses thing.currentDevice.id
   */
  const handleRemoveDeviceClicked = async () => {
    const { isConfirmed } = await openModal();
    if (isConfirmed) {
      // Track deletion event for analytics
      trackCustomEvents(`Delete Device`, {
        deviceId:
          product === APP_MOBILE.DRIVE
            ? thing.device.id // DRIVE uses device property
            : thing.currentDevice.id, // Other products use currentDevice
        thingId: thing.id
      });

      // Call parent callback to handle actual device deletion
      await onRemoveDevice({
        deviceId:
          product === APP_MOBILE.DRIVE
            ? thing.device.id
            : thing.currentDevice.id,
        thingId: thing.id
      });
    }
  };

  // ==========================================
  // COMPONENT SELECTION LOGIC
  // ==========================================

  /**
   * Renders the appropriate thing details component based on product type
   * Each product type has its own specialized details component:
   *
   * - DRIVE: VehicleDetails (for vehicles/cars)
   * - PETS: PetDetails (for pets with breed, age, etc.)
   * - Default: ThingDetails (generic thing information)
   *
   * All callbacks are passed through to enable child components to trigger actions
   *
   * @param {Object} thing - Thing object to render
   * @returns {JSX.Element} Appropriate details component
   */
  const thingComponent = thing => {
    // Special handling for DRIVE products
    if (product === APP_MOBILE.DRIVE && thing?.product === APP_MOBILE.DRIVE) {
      return (
        <VehicleDetails
          key={thing.id}
          vehicle={thing}
          onRemoveDevice={handleRemoveDeviceClicked}
          onAddDevice={onAddDevice}
          onRemoveThing={handleRemoveThingClicked}
        />
      );
    }

    // Switch based on thing type for other products
    switch (thing.thingType) {
      case THING_TYPES.PET:
        return (
          <PetDetails
            key={thing.id}
            pet={thing}
            onRemoveDevice={handleRemoveDeviceClicked}
            onAddDevice={onAddDevice}
            onRemoveThing={handleRemoveThingClicked}
          />
        );
      default:
        // Generic thing details for trackers, watches, etc.
        return (
          <ThingDetails
            thing={thing}
            onRemoveDevice={handleRemoveDeviceClicked}
            onAddDevice={onAddDevice}
            onRemoveThing={handleRemoveThingClicked}
          />
        );
    }
  };

  // ==========================================
  // MAIN RENDER
  // ==========================================

  return (
    <div className={style.thing_container}>
      <div className={style.container_tab}>
        {/* ========== THING DETAILS COLUMN ========== */}
        <div className={style.column}>
          {/* THING HEADER - Name, avatar, status, and action buttons */}
          <div className={style.heading_row}>
            {/* Left side - Thing name with optional avatar/image */}
            <div className={style.heading_with_image}>
              {/* Avatar/Image display - only for PETS and DRIVE products */}
              <Feature Products={[APP_MOBILE.PETS, APP_MOBILE.DRIVE]}>
                {(thing.frontImageUrl || thing.avatarUrl) && (
                  <div className={style.row}>
                    <div className={style.avatar_container}>
                      <div
                        style={{
                          backgroundImage:
                            'url(' +
                            (thing.avatarUrl || thing.frontImageUrl) +
                            ')'
                        }}
                        className={style.avatar}
                      />
                    </div>
                  </div>
                )}
              </Feature>

              {/* Thing name and status */}
              <TmoH3 className={style.heading} flush>
                {thing.name}
                <span className={style.status_mark}>
                  <StatusMark status={thing?.status?.toLowerCase()} />
                </span>
              </TmoH3>
            </div>

            {/* Right side - Action buttons */}
            <div className={style.action}>
              {/* Raw data viewer - shows full thing object in modal */}
              <RawData
                rawObject={thing}
                title={`Full ${thing.thingType ? thing.thingType : ''} Details`}
                tooltip={`Full ${
                  thing.thingType ? thing.thingType : ''
                } Details`}
                type=" "
                usePortal={false}
                modalType={'slide'}
                buttonType="magenta_primary"
              />

              {/* DRIVE-specific navigation buttons */}
              <Feature isUserDetails Products={[APP_MOBILE.DRIVE]}>
                {/* Navigate to trips page */}
                <TmoButton
                  onClick={() => history.push(`things/${thing.id}/trips`)}
                  type="magenta_primary"
                  tooltip="Trips"
                  icon={<ReactSVG src={trips} />}
                />

                {/* Navigate to device events page */}
                <TmoButton
                  onClick={() =>
                    history.push(
                      `things/${thing.id}/device/${
                        thing.currentDevice
                          ? thing.currentDevice.id
                          : thing.device.id
                      }/events`
                    )
                  }
                  className={style.action_button}
                  type="magenta_primary"
                  tooltip="Events"
                  icon={<ReactSVG src={events} />}
                />
              </Feature>

              {/* Roadside Services - only for enrolled DRIVE devices */}
              {thing?.device?.roadsideStatus === 'ENROLLED' && (
                <>
                  {/* Navigate to roadside services */}
                  <TmoButton
                    onClick={() =>
                      history.push(`devices/${thing.id}/roadside-services`)
                    }
                    type="magenta_primary"
                    tooltip="Roadside Services"
                    icon={<ReactSVG src={roadSide} />}
                  />

                  {/* Add roadside services component */}
                  <AddDeviceRoadsideServices thingId={thing.id} />
                </>
              )}

              {/* WATCH-specific functionality */}
              <Feature isUserDetails Products={[APP_MOBILE.WATCH]}>
                {/* TODO: Add School Mode Feature */}

                {/* Navigate to device logs if device exists */}
                {thing.id &&
                  ((thing.currentDevice && thing.currentDevice.id) ||
                    (thing.device && thing.device.id)) && (
                    <TmoButton
                      onClick={() =>
                        history.push(
                          `things/${thing.id}/device/${
                            thing.currentDevice
                              ? thing.currentDevice.id
                              : thing.device.id
                          }/logs`
                        )
                      }
                      type="magenta_primary"
                      tooltip="Logs"
                      icon={<ReactSVG src={logs} />}
                    />
                  )}
              </Feature>

              {/* Delete thing button */}
              <TmoButton
                onClick={handleRemoveThingClicked}
                type="magenta_secondary"
                tooltip="Delete"
                icon={<ReactSVG src={delete_icon} />}
              />

              {/* Confirmation modal for thing deletion */}
              <ConfirmationModalV2
                message={
                  <div className={style.label}>
                    {`Are you sure you want to remove the ${thing.thingType} `}
                    <span className={style.value}>{thing.name}</span>?
                  </div>
                }
                title={`Delete ${thing.thingType}`}
                acceptButtonText={`Delete ${thing.thingType}`}
                type="danger"
                usePortal={false}
                confirmInput
                modalType="delete"
              />
            </div>
          </div>

          {/* THING DETAILS CONTAINER */}
          <div className={style.thing_details_container}>
            {/* Product-specific thing details component */}
            {thingComponent(thing)}

            {/* WATCH TASKS SECTION - Only for WATCH products */}
            <Feature isUserDetails Products={[APP_MOBILE.WATCH]}>
              <div className={style.items}>
                <label className={style.label}>Tasks</label>

                {/* Expandable tasks list */}
                <ExpandableText
                  triggerText="View"
                  closeText="Close"
                  triggerIcon={<FaCaretDown />}
                >
                  {/* Fetch and display tasks */}
                  <Fetcher
                    key={refreshKey} // Refresh when refreshKey changes
                    action={actionUserTaskCallback}
                    onLoad={setUserTasks}
                    render={() =>
                      userTasks?.length > 0 ? (
                        <>
                          {/* Render each task */}
                          {userTasks.map(userTask => (
                            <TaskDetails
                              key={userTask.id || userTask.taskId} // Ensure unique key
                              userTask={userTask}
                              forceRefresh={forceRefresh} // Allow task to trigger refresh
                            />
                          ))}
                        </>
                      ) : (
                        <span className={style.message}>
                          No Tasks are available
                        </span>
                      )
                    }
                  />
                </ExpandableText>
              </div>
            </Feature>
          </div>
        </div>

        {/* ========== DEVICE DETAILS SECTION  ========== */}
        <DeviceDetailsSection
          thing={thing}
          product={product}
          onAddDevice={onAddDevice}
          handleRemoveDeviceClicked={handleRemoveDeviceClicked}
        />

        {/* ========== DEVICE TWIN DETAILS ========== */}
        {/* Only show for IoT products that support device twin functionality */}
        {IOT_PRODUCTS.includes(productType) && (
          <Fetcher
            action={actionCallback}
            onLoad={setDeviceTwin}
            render={() =>
              deviceTwin ? (
                <div className={style.column}>
                  <div className={style.heading_row}>
                    <TmoH3 className={style.heading} flush>
                      DEVICE TWIN DETAILS
                    </TmoH3>

                    {/* Raw data viewer for device twin */}
                    <div className={classNames(style.row, style.action)}>
                      <RawData
                        rawObject={deviceTwin}
                        title="Device Twin Details"
                        tooltip="Device Twin Details"
                        type=" "
                        usePortal={false}
                        modalType={'slide'}
                        buttonType="magenta_primary"
                      />
                    </div>
                  </div>

                  {/* Device twin component displays telemetry data */}
                  <DeviceTwin device={deviceTwin} />
                </div>
              ) : null
            }
          />
        )}

        {/* ========== HARDWARE DETAILS ========== */}
        {/* Only show for PETS1 device model - specific hardware diagnostics */}
        {thing?.currentDevice &&
          thing.currentDevice?.product === DEVICE_MODELS.PETS1 && (
            <div className={style.column}>
              <div className={style.heading_row}>
                <TmoH3 className={style.heading} flush>
                  HARDWARE DETAILS
                </TmoH3>

                {/* Hardware details header with IMEI */}
                <HardwareDetailsHeader imei={thing.currentDevice?.hardwareId} />
              </div>

              {/* Hardware details grid showing diagnostic information */}
              <HardwareDetailsGrid
                hardware={thing.currentDevice}
                imei={thing.currentDevice?.hardwareId}
              />
            </div>
          )}
      </div>

      {/* ========== SENSOR DETAILS ========== */}
      {/* Show sensor information for IoT products outside main container */}
      <Feature
        isUserDetails
        Products={[APP_MOBILE.PETS, APP_MOBILE.TRACKER, APP_MOBILE.WATCH]}
      >
        <SensorDetails thing={thing} />
      </Feature>
    </div>
  );
}

// Export component with modal functionality provided by HOC
export default withModal(Thing);
